/**
 * Correo de trabajo (BHM-CLD-REQ-043). Cliente de /mail/* y /push/*. Nunca recibe tokens de Google.
 */
import { api } from '../utils/api';

export type MailPriority = 'URGENTE' | 'IMPORTANTE' | 'NORMAL' | 'BAJO_VALOR' | 'SPAM';
export type MailAccountStatus = 'CONNECTED' | 'DISCONNECTED' | 'REAUTH_REQUIRED' | 'ERROR' | 'UNVERIFIED';

export const PRIORITY_LABEL: Record<MailPriority, string> = { URGENTE: 'Urgente', IMPORTANTE: 'Importante', NORMAL: 'Normal', BAJO_VALOR: 'Bajo valor', SPAM: 'Spam' };
export const PRIORITY_CLS: Record<MailPriority, string> = {
  URGENTE: 'border-[#F43F5E]/40 bg-[#F43F5E]/10 text-[#F43F5E]',
  IMPORTANTE: 'border-[#FF5E1E]/40 bg-[#FF5E1E]/10 text-[#FF8A5B]',
  NORMAL: 'border-outline-variant text-on-surface-variant',
  BAJO_VALOR: 'border-outline text-outline',
  SPAM: 'border-outline text-outline',
};
export const SIGNAL_LABEL: Record<string, string> = {
  REQUIERE_RESPUESTA: 'Requiere respuesta',
  CLIENTE_ESPERANDO: 'Cliente esperando',
  CLIENTE: 'Cliente',
  PLAZO_DETECTADO: 'Plazo detectado',
  VENCIMIENTO_PROXIMO: 'Vencimiento próximo',
  RELACIONADO_PROYECTO: 'Proyecto',
  RELACIONADO_TAREA: 'Tarea',
  IMPORTANTE_GMAIL: 'Importante (Gmail)',
  REMITENTE_PRIORIZADO: 'Remitente priorizado',
  REMITENTE_NO_PRIORIZADO: 'Remitente no priorizado',
  CORRECCION_USUARIO: 'Corregido',
  MASIVO: 'Masivo',
  AUTOMATICO: 'Automático',
  PROMOCION: 'Promoción',
  SPAM_GMAIL: 'Spam (Gmail)',
  INTERNO: 'Interno',
};
export const STATUS_LABEL: Record<MailAccountStatus, string> = { CONNECTED: 'Conectado', DISCONNECTED: 'Sin conectar', REAUTH_REQUIRED: 'Reautorización necesaria', ERROR: 'Error de Google', UNVERIFIED: 'Sin verificar' };

export interface MailStatus {
  configured: boolean; status: MailAccountStatus; email: string | null; lastSyncAt: string | null; lastError: string | null; domain: string;
  permissions: { read: boolean; send: boolean; link: boolean; orgView: boolean };
}
export interface MailItem {
  id: string; gmailId: string; threadId: string; from: { email: string; name: string | null }; subject: string; snippet: string; receivedAt: string; unread: boolean;
  priority: MailPriority; signals: string[]; reasons: string[]; corrected: boolean;
  project: { id: string; name: string | null } | null; task: { id: string; title: string | null } | null;
  employee?: { id: string; name: string };
}
export interface MailSummary { requiresAction: number; urgent: number; important: number; fromClients: number; clientWaiting: number; taskDueSoon: number; pendingReply: number }
export interface MailPriorityView { account: { email: string; status: string; lastSyncAt: string | null }; syncError: string | null; summary: MailSummary; items: MailItem[]; lowValueCount: number; spamCount: number }
export interface ThreadView { threadId: string; messages: { gmailId: string; from: { email: string; name: string | null } | null; to: string; cc: string; subject: string; date: string; text: string; attachments: { attachmentId: string; filename: string; mimeType: string; size: number }[]; sent: boolean }[] }
export interface MailDraft { id: string; toEmails: string; subject: string; body: string; status: string; threadId: string | null; projectId: string | null; taskId: string | null; sentAt: string | null; error: string | null }
export interface Candidates { kind: 'ONE' | 'SEVERAL' | 'NONE'; candidates: { projectId: string; name: string; why: string }[]; message: string }

const g = async <T,>(url: string, params?: Record<string, unknown>) => (await api.get<T>(url, { params })).data;
const p = async <T,>(url: string, body?: unknown) => (await api.post<T>(url, body)).data;

export const mailApi = {
  status: () => g<MailStatus>('/mail/status'),
  connect: () => p<{ url: string }>('/mail/connect'),
  disconnect: () => p<{ status: string }>('/mail/disconnect'),
  priority: () => g<MailPriorityView>('/mail/priority'),
  discarded: () => g<MailItem[]>('/mail/discarded'),
  search: (q: string, mailbox: 'INBOX' | 'SENT' | 'ALL' = 'ALL') => g<{ gmailId: string; threadId: string; id: string | null; from: { email: string; name: string | null } | null; subject: string; snippet: string; receivedAt: string; sent: boolean; priority: MailPriority | null }[]>('/mail/search', { q, mailbox }),
  thread: (threadId: string) => g<ThreadView>(`/mail/threads/${encodeURIComponent(threadId)}`),
  attachmentUrl: (gmailId: string, attId: string) => `/mail/messages/${encodeURIComponent(gmailId)}/attachments/${encodeURIComponent(attId)}`,
  classify: (id: string, priority: MailPriority | null) => p<MailItem>(`/mail/messages/${id}/classify`, { priority }),
  candidates: (id: string) => g<Candidates>(`/mail/messages/${id}/project-candidates`),
  link: (id: string, projectId: string | null, taskId?: string | null) => p<MailItem>(`/mail/messages/${id}/link`, { projectId, taskId: taskId ?? null }),
  taskProposal: (id: string) => g<{ proposed: boolean; title?: string; projectId?: string | null; canCreate?: boolean; reason?: string }>(`/mail/messages/${id}/task-proposal`),
  createTask: (id: string, d: { projectId: string; processId?: string; title: string; dueDate?: string }) => p<{ task: { id: string } }>(`/mail/messages/${id}/task`, d),
  rule: (pattern: string, action: 'PRIORITIZE' | 'DEPRIORITIZE', scope: 'USER' | 'ORG' = 'USER') => p('/mail/rules', { pattern, action, scope }),
  createDraft: (d: { replyToId?: string; to?: string[]; subject?: string; body: string; projectId?: string; taskId?: string; executionId?: string }) => p<MailDraft>('/mail/drafts', d),
  updateDraft: async (id: string, d: { to?: string[]; subject?: string; body?: string }) => (await api.put<MailDraft>(`/mail/drafts/${id}`, d)).data,
  send: (id: string) => p<MailDraft>(`/mail/drafts/${id}/send`, { confirm: true }),
  discard: (id: string) => p<MailDraft>(`/mail/drafts/${id}/discard`),
  drafts: () => g<MailDraft[]>('/mail/drafts'),
  adminOverview: () => g<{ coverage: { userId: string; name: string | null; email: string; status: string; lastSyncAt: string | null }[]; connected: number; domainWideDelegation: string; orgView: string; message?: string; summary?: MailSummary; items: MailItem[]; byEmployee: { name: string; count: number }[]; byProject: { name: string; count: number }[]; byClient: { name: string; count: number }[] }>('/mail/admin/overview'),
  pushStatus: () => g<{ enabled: boolean; publicKey: string | null; subscriptions: number }>('/push/status'),
  pushSubscribe: (s: { endpoint: string; p256dh: string; auth: string }) => p('/push/subscribe', s),
  pushTest: () => p<{ delivered: number }>('/push/test'),
};

/** Frase de «Prioritario hoy» construida solo con recuentos reales. */
export function priorityPhrase(s: MailSummary): string {
  if (!s.requiresAction) return 'No tienes correo prioritario pendiente.';
  const parts: string[] = [];
  if (s.fromClients) parts.push(`${s.fromClients} de clientes`);
  if (s.taskDueSoon) parts.push(`${s.taskDueSoon} relacionado${s.taskDueSoon > 1 ? 's' : ''} con tareas que vencen pronto`);
  if (s.pendingReply) parts.push(`${s.pendingReply} pendiente${s.pendingReply > 1 ? 's' : ''} de respuesta`);
  const head = `Tienes ${s.requiresAction} correo${s.requiresAction > 1 ? 's' : ''} ${s.requiresAction > 1 ? 'importantes' : 'importante'} que requiere${s.requiresAction > 1 ? 'n' : ''} actuación`;
  return parts.length ? `${head}: ${parts.join(', ')}.` : `${head}.`;
}

export function mailErrorCode(x: unknown): string | null {
  const d = (x as { response?: { data?: { code?: string } } })?.response?.data;
  return d?.code ?? null;
}
